/**
 * Runtime image rewrite (client-only, app-wide). Two independent fix-ups, applied
 * to every <img src>/<img srcset>, inline `style="background-image:url(...)"`,
 * same-origin iframes (CMS pages via srcDoc), and HTML strings:
 *
 *  1. Colour swatches — any `…/images/colors/<name>.png` (on any host) is
 *     rewritten to the local static path `/images/colors/<name>.png`
 *     (self-hosted under static/images/colors).
 *
 *  2. Host de-clobber — `serve.js` substitutes the per-request API base into the
 *     served HTML/JS. On the internal preview host (and any alternate public
 *     host) that API base differs from the storefront's canonical image CDN; and
 *     because the hardcoded image base literal (the canonical image origin)
 *     happens to equal `GATSBY_CLIENT_TARGET`, that substitution also rewrites
 *     product-image hosts (e.g. `<canonical-origin>/images/type6/…` →
 *     `https://grm-bff-…/images/type6/…`, which 404s). Here we force any
 *     `/images/…` URL whose host equals the runtime API base back to the
 *     canonical image origin (see CANONICAL_IMAGE_ORIGIN in imageHostRewrite.ts),
 *     so product images ALWAYS load from the CDN regardless of which host served
 *     the page. Only the API-base host is touched — genuine third-party/CDN image
 *     hosts are left exactly as-is.
 *
 * All OTHER image URLs are left EXACTLY as-is.
 *
 * Installed once from gatsby-browser.js `onClientEntry`.
 */

import { rewriteColors, rewriteStampedImageHosts } from './imageHostRewrite';

/**
 * Runtime API-base hosts that serve.js may have stamped over the image CDN host.
 * On the canonical public host the API base IS the canonical host, so the set is
 * empty and nothing is rewritten.
 *
 * @returns {Set<string>} hosts to de-clobber (never includes www.germanos.gr)
 */
function stampedHosts() {
  const hosts = new Set();
  if (typeof window === 'undefined') return hosts;
  const add = (u) => {
    try {
      if (u) hosts.add(new URL(u).host);
    } catch (e) {
      /* ignore invalid URL */
    }
  };
  add(window.__API_BASE__);
  add(window.__PREVIEW_API_BASE__);
  hosts.delete('www.germanos.gr'); // the canonical host is never "wrong"
  return hosts;
}

// Memoised once the API-base globals are populated (they are stamped inline in the
// SSR <head> before the app bundle loads, so they are ready by first sweep).
let cachedHosts = null;
function currentHosts() {
  if (!cachedHosts || cachedHosts.size === 0) cachedHosts = stampedHosts();
  return cachedHosts;
}

/** Both passes, in order: colours → local, then stamped product-image hosts → CDN. */
function rewrite(value) {
  return rewriteStampedImageHosts(rewriteColors(value), currentHosts());
}

function fixImg(img) {
  const src = img.getAttribute('src');
  if (src) {
    const n = rewrite(src);
    if (n !== src) img.setAttribute('src', n);
  }
  const srcset = img.getAttribute('srcset');
  if (srcset) {
    const n = rewrite(srcset);
    if (n !== srcset) img.setAttribute('srcset', n);
  }
}

function fixBackground(el) {
  if (!el.getAttribute) return;
  const style = el.getAttribute('style');
  if (style && style.indexOf('/images/') !== -1 && style.indexOf('url(') !== -1) {
    const n = rewrite(style);
    if (n !== style) el.setAttribute('style', n);
  }
}

function sweep(root) {
  if (!root || !root.querySelectorAll) return;
  root.querySelectorAll('img').forEach(fixImg);
  root.querySelectorAll('[style*="/images/"]').forEach(fixBackground);
}

function observe(doc) {
  if (!doc || !doc.body) return;
  sweep(doc);
  const view = doc.defaultView || window;
  if (!view || !view.MutationObserver) return;
  const mo = new view.MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type === 'attributes' && m.target && m.target.nodeType === 1) {
        if (m.target.tagName === 'IMG') fixImg(m.target);
        else fixBackground(m.target);
      } else if (m.addedNodes) {
        m.addedNodes.forEach((node) => {
          if (node.nodeType !== 1) return;
          if (node.tagName === 'IMG') fixImg(node);
          else if (node.querySelectorAll) sweep(node);
          if (node.tagName === 'IFRAME') hookIframe(node);
        });
      }
    }
  });
  mo.observe(doc.body, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: ['src', 'srcset', 'style'],
  });
}

function hookIframe(iframe) {
  const tryDoc = () => {
    try {
      const d = iframe.contentDocument;
      if (d && d.body) observe(d);
    } catch (e) {
      /* cross-origin iframe — cannot/shouldn't touch */
    }
  };
  iframe.addEventListener('load', tryDoc);
  tryDoc();
}

/**
 * Rewrite colour-swatch image URLs → local static path inside an HTML *string*.
 * Targets only image references — `src`/`srcset` attributes and CSS `url(...)`
 * (inline styles + <style> blocks) — and leaves <a href> links and every other
 * image URL untouched. Used to fix iframe `srcDoc` content deterministically.
 */
export function rewriteHtmlImageHosts(html) {
  if (!html || html.indexOf('/images/colors/') === -1) return html;
  return html
    .replace(/(\b(?:src|srcset)\s*=\s*["'])([^"']+)(["'])/gi,
      (m, p, u, q) => { const n = rewriteColors(u); return n !== u ? p + n + q : m; })
    .replace(/url\((\s*['"]?)([^'")]+)(['"]?\s*)\)/gi,
      (m, a, u, b) => { const n = rewriteColors(u); return n !== u ? 'url(' + a + n + b + ')' : m; });
}

/** Install the image rewriter on the main document and any (current/future) iframes. */
export function installCloudImageRewrite() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const start = () => {
    observe(document);
    document.querySelectorAll('iframe').forEach(hookIframe);
  };
  if (document.body) start();
  else window.addEventListener('DOMContentLoaded', start);
}
